import {
	AchLimitOverrideInfo,
	CardLimitOverrideInfo,
	CheckLimitOverrideInfo,
	WireLimitOverrideInfo,
	CheckPaymentLimitOverrideInfo,
	LimitOverrideType,
	Limits,
} from "../../../resources/account"

const {
	AchDailyCredit,
	AchDailyDebit,
	AchMonthlyCredit,
	AchMonthlyDebit,
	CardDailyPurchase,
	CardDailyTransaction,
	CardDailyDeposit,
	CardDailyWithdrawal,
	WireDailyTransfer,
	WireMonthlyTransfer,
	CheckDepositDaily,
	CheckDepositMonthly,
	CheckPaymentDaily,
	CheckPaymentMonthly,
} = LimitOverrideType

type OverrideInfo =
	| AchLimitOverrideInfo
	| CardLimitOverrideInfo
	| CheckLimitOverrideInfo
	| WireLimitOverrideInfo
	| CheckPaymentLimitOverrideInfo

export function isOverrideActive(override?: OverrideInfo): boolean {
	if (!override) return false
	return override.status === "Active"
}

export function buildAchOverrideInfoProp(
	override: AchLimitOverrideInfo | undefined,
	limitType: LimitOverrideType,
	depositProductLimit: number
): {endDate?: string; depositProductLimit: number} | undefined {
	if (!override || !isOverrideActive(override)) return undefined

	let overrideAmount: number | undefined
	switch (limitType) {
		case AchDailyCredit:
			overrideAmount = override.values.dailyCredit
			break
		case AchDailyDebit:
			overrideAmount = override.values.dailyDebit
			break
		case AchMonthlyCredit:
			overrideAmount = override.values.monthlyCredit
			break
		case AchMonthlyDebit:
			overrideAmount = override.values.monthlyDebit
			break
		default:
			return undefined
	}

	if (overrideAmount === undefined) return undefined

	return {
		endDate: override.expiredAt,
		depositProductLimit,
	}
}

export function getEffectiveAchLimit(
	baseLimit: number,
	override: AchLimitOverrideInfo | undefined,
	limitType: LimitOverrideType
): number {
	if (!override || !isOverrideActive(override)) return baseLimit

	switch (limitType) {
		case AchDailyCredit:
			return override.values.dailyCredit ?? baseLimit
		case AchDailyDebit:
			return override.values.dailyDebit ?? baseLimit
		case AchMonthlyCredit:
			return override.values.monthlyCredit ?? baseLimit
		case AchMonthlyDebit:
			return override.values.monthlyDebit ?? baseLimit
		default:
			return baseLimit
	}
}

export function buildCardOverrideInfoProp(
	override: CardLimitOverrideInfo | undefined,
	limitType: LimitOverrideType,
	depositProductLimit: number
): {endDate?: string; depositProductLimit: number} | undefined {
	if (!override || !isOverrideActive(override)) return undefined

	let overrideAmount: number | undefined
	switch (limitType) {
		case CardDailyPurchase:
			overrideAmount = override.values.dailyPurchase
			break
		case CardDailyTransaction:
			overrideAmount = override.values.dailyCardTransaction
			break
		case CardDailyDeposit:
			overrideAmount = override.values.dailyDeposit
			break
		case CardDailyWithdrawal:
			overrideAmount = override.values.dailyWithdrawal
			break
		default:
			return undefined
	}

	if (overrideAmount === undefined) return undefined

	return {
		endDate: override.expiredAt,
		depositProductLimit,
	}
}

export function getEffectiveCardLimit(
	baseLimit: number,
	override: CardLimitOverrideInfo | undefined,
	limitType: LimitOverrideType
): number {
	if (!override || !isOverrideActive(override)) return baseLimit

	switch (limitType) {
		case CardDailyPurchase:
			return override.values.dailyPurchase ?? baseLimit
		case CardDailyTransaction:
			return override.values.dailyCardTransaction ?? baseLimit
		case CardDailyDeposit:
			return override.values.dailyDeposit ?? baseLimit
		case CardDailyWithdrawal:
			return override.values.dailyWithdrawal ?? baseLimit
		default:
			return baseLimit
	}
}

export function buildWireOverrideInfoProp(
	override: WireLimitOverrideInfo | undefined,
	limitType: LimitOverrideType,
	depositProductLimit: number
): {endDate?: string; depositProductLimit: number} | undefined {
	if (!override || !isOverrideActive(override)) return undefined

	let overrideAmount: number | undefined
	switch (limitType) {
		case WireDailyTransfer:
			overrideAmount = override.values.daily
			break
		case WireMonthlyTransfer:
			overrideAmount = override.values.monthly
			break
		default:
			return undefined
	}

	if (overrideAmount === undefined) return undefined

	return {
		endDate: override.expiredAt,
		depositProductLimit,
	}
}

export function getEffectiveWireLimit(
	baseLimit: number,
	override: WireLimitOverrideInfo | undefined,
	limitType: LimitOverrideType
): number {
	if (!override || !isOverrideActive(override)) return baseLimit

	switch (limitType) {
		case WireDailyTransfer:
			return override.values.daily ?? baseLimit
		case WireMonthlyTransfer:
			return override.values.monthly ?? baseLimit
		default:
			return baseLimit
	}
}

export function buildCheckDepositOverrideInfoProp(
	override: CheckLimitOverrideInfo | undefined,
	limitType: LimitOverrideType,
	depositProductLimit: number
): {endDate?: string; depositProductLimit: number} | undefined {
	if (!override || !isOverrideActive(override)) return undefined

	let overrideAmount: number | undefined
	switch (limitType) {
		case CheckDepositDaily:
			overrideAmount = override.values.daily
			break
		case CheckDepositMonthly:
			overrideAmount = override.values.monthly
			break
		default:
			return undefined
	}

	if (overrideAmount === undefined) return undefined

	return {
		endDate: override.expiredAt,
		depositProductLimit,
	}
}

export function getEffectiveCheckDepositLimit(
	baseLimit: number,
	override: CheckLimitOverrideInfo | undefined,
	limitType: LimitOverrideType
): number {
	if (!override || !isOverrideActive(override)) return baseLimit

	switch (limitType) {
		case CheckDepositDaily:
			return override.values.daily ?? baseLimit
		case CheckDepositMonthly:
			return override.values.monthly ?? baseLimit
		default:
			return baseLimit
	}
}

export function buildCheckPaymentOverrideInfoProp(
	override: CheckPaymentLimitOverrideInfo | undefined,
	limitType: LimitOverrideType,
	depositProductLimit: number
): {endDate?: string; depositProductLimit: number} | undefined {
	if (!override || !isOverrideActive(override)) return undefined

	let overrideAmount: number | undefined
	switch (limitType) {
		case CheckPaymentDaily:
			overrideAmount = override.values.daily
			break
		case CheckPaymentMonthly:
			overrideAmount = override.values.monthly
			break
		default:
			return undefined
	}

	if (overrideAmount === undefined) return undefined

	return {
		endDate: override.expiredAt,
		depositProductLimit,
	}
}

export function getEffectiveCheckPaymentLimit(
	baseLimit: number,
	override: CheckPaymentLimitOverrideInfo | undefined,
	limitType: LimitOverrideType
): number {
	if (!override || !isOverrideActive(override)) return baseLimit

	switch (limitType) {
		case CheckPaymentDaily:
			return override.values.daily ?? baseLimit
		case CheckPaymentMonthly:
			return override.values.monthly ?? baseLimit
		default:
			return baseLimit
	}
}

export function getLimitValueByType(limits: Limits, limitType: LimitOverrideType): number {
	switch (limitType) {
		case CardDailyPurchase:
			return limits.attributes.card.limits.dailyPurchase
		case CardDailyTransaction:
			return limits.attributes.card.limits.dailyCardTransaction
		case CardDailyDeposit:
			return limits.attributes.card.limits.dailyDeposit
		case CardDailyWithdrawal:
			return limits.attributes.card.limits.dailyWithdrawal
		case AchDailyCredit:
			return limits.attributes.ach.limits.dailyCredit
		case AchDailyDebit:
			return limits.attributes.ach.limits.dailyDebit
		case AchMonthlyCredit:
			return limits.attributes.ach.limits.monthlyCredit
		case AchMonthlyDebit:
			return limits.attributes.ach.limits.monthlyDebit
		case WireDailyTransfer:
			return limits.attributes.wire.limits.dailyTransfer
		case WireMonthlyTransfer:
			return limits.attributes.wire.limits.monthlyTransfer
		case CheckDepositDaily:
			return limits.attributes.checkDeposit.limits.daily
		case CheckDepositMonthly:
			return limits.attributes.checkDeposit.limits.monthly
		case CheckPaymentDaily:
			return limits.attributes.checkPayment.limits.dailySent ?? 0
		case CheckPaymentMonthly:
			return limits.attributes.checkPayment.limits.monthlySent ?? 0
		default:
			return 0
	}
}

export function getExistingOverrideFromLimits(
	limits: Limits,
	limitType: LimitOverrideType
): {amount: number; expiredAt?: string} | undefined {
	const achOverride = limits.attributes.ach.override
	const cardOverride = limits.attributes.card.override
	const wireOverride = limits.attributes.wire.override
	const checkDepositOverride = limits.attributes.checkDeposit.override
	const checkPaymentOverride = limits.attributes.checkPayment.override

	const getAchOverrideAmount = (): number | undefined => {
		if (!achOverride || !isOverrideActive(achOverride)) return undefined
		switch (limitType) {
			case AchDailyCredit:
				return achOverride.values.dailyCredit
			case AchDailyDebit:
				return achOverride.values.dailyDebit
			case AchMonthlyCredit:
				return achOverride.values.monthlyCredit
			case AchMonthlyDebit:
				return achOverride.values.monthlyDebit
			default:
				return undefined
		}
	}

	const getCardOverrideAmount = (): number | undefined => {
		if (!cardOverride || !isOverrideActive(cardOverride)) return undefined
		switch (limitType) {
			case CardDailyPurchase:
				return cardOverride.values.dailyPurchase
			case CardDailyTransaction:
				return cardOverride.values.dailyCardTransaction
			case CardDailyDeposit:
				return cardOverride.values.dailyDeposit
			case CardDailyWithdrawal:
				return cardOverride.values.dailyWithdrawal
			default:
				return undefined
		}
	}

	const getWireOverrideAmount = (): number | undefined => {
		if (!wireOverride || !isOverrideActive(wireOverride)) return undefined
		switch (limitType) {
			case WireDailyTransfer:
				return wireOverride.values.daily
			case WireMonthlyTransfer:
				return wireOverride.values.monthly
			default:
				return undefined
		}
	}

	const getCheckDepositOverrideAmount = (): number | undefined => {
		if (!checkDepositOverride || !isOverrideActive(checkDepositOverride)) return undefined
		switch (limitType) {
			case CheckDepositDaily:
				return checkDepositOverride.values.daily
			case CheckDepositMonthly:
				return checkDepositOverride.values.monthly
			default:
				return undefined
		}
	}

	const getCheckPaymentOverrideAmount = (): number | undefined => {
		if (!checkPaymentOverride || !isOverrideActive(checkPaymentOverride)) return undefined
		switch (limitType) {
			case CheckPaymentDaily:
				return checkPaymentOverride.values.daily
			case CheckPaymentMonthly:
				return checkPaymentOverride.values.monthly
			default:
				return undefined
		}
	}

	const achAmount = getAchOverrideAmount()
	if (achAmount !== undefined) {
		return {amount: achAmount, expiredAt: achOverride?.expiredAt}
	}

	const cardAmount = getCardOverrideAmount()
	if (cardAmount !== undefined) {
		return {amount: cardAmount, expiredAt: cardOverride?.expiredAt}
	}

	const wireAmount = getWireOverrideAmount()
	if (wireAmount !== undefined) {
		return {amount: wireAmount, expiredAt: wireOverride?.expiredAt}
	}

	const checkDepositAmount = getCheckDepositOverrideAmount()
	if (checkDepositAmount !== undefined) {
		return {amount: checkDepositAmount, expiredAt: checkDepositOverride?.expiredAt}
	}

	const checkPaymentAmount = getCheckPaymentOverrideAmount()
	if (checkPaymentAmount !== undefined) {
		return {amount: checkPaymentAmount, expiredAt: checkPaymentOverride?.expiredAt}
	}

	return undefined
}
